import { useMemo, useState } from 'react';
import {
  Typography,
  Table,
  Space,
  Tag,
  Select,
  Button,
  Drawer,
  Timeline,
  Form,
  Input,
  Divider,
  message,
  Empty,
  Descriptions,
  Tooltip,
  Checkbox,
} from 'antd';
import {
  CustomerServiceOutlined,
  FieldTimeOutlined,
  LinkOutlined,
  TeamOutlined,
  UserOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { useAdminSupportTickets, useTicketMessages, useUpdateSupportTicket, useCreateTicketMessage } from '../hooks/useSupportTickets';
import type { SupportTicketSummary } from '@/shared/types';
import { useAuthStore } from '@/features/auth/store/authStore';

const { Title, Paragraph, Text } = Typography;

const statusOptions = [
  { value: 'open', label: 'Aberto', color: 'blue' },
  { value: 'in_progress', label: 'Em andamento', color: 'gold' },
  { value: 'waiting_client', label: 'Aguardando cliente', color: 'purple' },
  { value: 'resolved', label: 'Resolvido', color: 'green' },
  { value: 'closed', label: 'Fechado', color: 'default' },
];

const priorityOptions = [
  { value: 'low', label: 'Baixa', color: 'default' },
  { value: 'medium', label: 'Média', color: 'gold' },
  { value: 'high', label: 'Alta', color: 'red' },
];

const categoryOptions = [
  { value: 'technical', label: 'Técnico' },
  { value: 'financial', label: 'Financeiro' },
  { value: 'general', label: 'Geral' },
  { value: 'setup', label: 'Taxa de Setup' },
];

function statusTag(status: string) {
  const found = statusOptions.find((item) => item.value === status);
  return <Tag color={found?.color || 'default'}>{found?.label ?? status}</Tag>;
}

function priorityTag(priority: string) {
  const found = priorityOptions.find((item) => item.value === priority);
  return <Tag color={found?.color || 'default'}>{found?.label ?? priority}</Tag>;
}

export const AdminSupportPage: React.FC = () => {
  const [filters, setFilters] = useState({ status: ['open', 'in_progress'] as string[], priority: [] as string[], category: [] as string[] });
  const [selectedTicket, setSelectedTicket] = useState<SupportTicketSummary | null>(null);
  const [messageForm] = Form.useForm();
  const { user } = useAuthStore();

  const { data: tickets = [], isLoading } = useAdminSupportTickets(filters);
  const { data: messages = [], isLoading: loadingMessages } = useTicketMessages(selectedTicket?.id ?? null);
  const updateTicket = useUpdateSupportTicket();
  const createMessage = useCreateTicketMessage();

  const ticketStats = useMemo(() => {
    const total = tickets.length;
    const byStatus = filters.status?.length
      ? tickets.filter((item) => filters.status?.includes(item.status)).length
      : total;
    const highPriority = tickets.filter((item) => item.priority === 'high').length;
    return { total, byStatus, highPriority };
  }, [tickets, filters.status]);

  const columns: ColumnsType<SupportTicketSummary> = [
    {
      title: 'Chamado',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <Space direction="vertical" size={0} style={{ width: '100%' }}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Criado em {dayjs(record.created_at).format('DD/MM/YYYY HH:mm')}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Cliente',
      dataIndex: 'user',
      key: 'user',
      render: (value) => (
        <Space>
          <UserOutlined />
          <span>{value?.full_name || 'N/A'}</span>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => statusTag(status),
    },
    {
      title: 'Prioridade',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => priorityTag(priority),
    },
    {
      title: 'Categoria',
      dataIndex: 'category',
      key: 'category',
      render: (category) => (
        <Tag>{categoryOptions.find((item) => item.value === category)?.label ?? category}</Tag>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => setSelectedTicket(record)}>
            Detalhes
          </Button>
          {record.assigned_admin_id ? (
            <Tag color="green">Atribuído</Tag>
          ) : (
            <Button
              type="link"
              onClick={async () => {
                if (!user) return;
                await updateTicket.mutateAsync({ id: record.id, assigned_admin_id: user.id });
                message.success('Chamado atribuído a você.');
              }}
            >
              Assumir
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const handleStatusChange = async (status: SupportTicketSummary['status']) => {
    if (!selectedTicket) return;
    await updateTicket.mutateAsync({ id: selectedTicket.id, status });
    message.success('Status atualizado com sucesso.');
    setSelectedTicket((prev) => (prev ? { ...prev, status } : prev));
  };

  const handleSaveNotionLink = async (notion_page_url: string) => {
    if (!selectedTicket) return;
    await updateTicket.mutateAsync({ id: selectedTicket.id, notion_page_url });
    message.success('Link do Notion atualizado.');
    setSelectedTicket((prev) => (prev ? { ...prev, notion_page_url } : prev));
  };

  const handleSubmitInternalNote = async () => {
    const values = await messageForm.validateFields();
    if (!selectedTicket || !user) return;

    await createMessage.mutateAsync({
      ticket_id: selectedTicket.id,
      message: values.message,
      user_id: user.id,
      is_internal: values.is_internal ?? true,
      created_by_role: 'admin',
    });

    await updateTicket.mutateAsync({
      id: selectedTicket.id,
      last_reply_at: new Date().toISOString(),
      last_reply_by: user.id,
    });

    message.success('Resposta registrada.');
    messageForm.resetFields();
  };

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2} style={{ margin: 0 }}>
            Chamados de Suporte
          </Title>
          <Space>
            <Select
              mode="multiple"
              placeholder="Status"
              value={filters.status}
              style={{ minWidth: 180 }}
              onChange={(status) => setFilters((prev) => ({ ...prev, status }))}
              options={statusOptions}
            />
            <Select
              mode="multiple"
              placeholder="Prioridade"
              style={{ minWidth: 160 }}
              value={filters.priority}
              onChange={(priority) => setFilters((prev) => ({ ...prev, priority }))}
              options={priorityOptions}
            />
            <Select
              mode="multiple"
              placeholder="Categoria"
              style={{ minWidth: 160 }}
              value={filters.category}
              onChange={(category) => setFilters((prev) => ({ ...prev, category }))}
              options={categoryOptions}
            />
          </Space>
        </div>

        <Space size="large">
          <Tag icon={<CustomerServiceOutlined />} color="blue">
            {ticketStats.total} chamados
          </Tag>
          <Tag icon={<FieldTimeOutlined />} color="gold">
            {ticketStats.byStatus} ativos
          </Tag>
          <Tag icon={<TeamOutlined />} color="red">
            {ticketStats.highPriority} alta prioridade
          </Tag>
        </Space>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={tickets}
          loading={isLoading}
          locale={{ emptyText: 'Nenhum chamado encontrado' }}
          pagination={{ pageSize: 10 }}
        />
      </Space>

      <Drawer
        width={640}
        open={!!selectedTicket}
        onClose={() => setSelectedTicket(null)}
        title={selectedTicket ? selectedTicket.title : 'Detalhes do chamado'}
      >
        {!selectedTicket ? (
          <Empty description="Selecione um chamado" />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Status">
                <Select
                  value={selectedTicket.status}
                  style={{ width: 220 }}
                  onChange={handleStatusChange}
                  options={statusOptions}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Prioridade">{priorityTag(selectedTicket.priority)}</Descriptions.Item>
              <Descriptions.Item label="Categoria">
                {categoryOptions.find((item) => item.value === selectedTicket.category)?.label ?? selectedTicket.category}
              </Descriptions.Item>
              <Descriptions.Item label="Cliente">
                <Space direction="vertical" size={0}>
                  <Text strong>{selectedTicket.user?.full_name ?? 'N/A'}</Text>
                  <Text type="secondary">{selectedTicket.user?.company_name ?? 'Sem empresa'}</Text>
                </Space>
              </Descriptions.Item>
              
              {selectedTicket.category === 'setup' && (
                <>
                  <Descriptions.Item label="Status do Pagamento">
                    {selectedTicket.payment_status ? (
                      <Space direction="vertical" size={0}>
                        <Tag color={
                          selectedTicket.payment_status === 'RECEIVED' || selectedTicket.payment_status === 'CONFIRMED' 
                            ? 'green' 
                            : selectedTicket.payment_status === 'PENDING' 
                            ? 'gold' 
                            : 'red'
                        }>
                          {selectedTicket.payment_status === 'RECEIVED' || selectedTicket.payment_status === 'CONFIRMED' 
                            ? 'PAGO' 
                            : selectedTicket.payment_status === 'PENDING'
                            ? 'PENDENTE'
                            : selectedTicket.payment_status}
                        </Tag>
                        {selectedTicket.paid_at && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Pago em {dayjs(selectedTicket.paid_at).format('DD/MM/YYYY HH:mm')}
                          </Text>
                        )}
                      </Space>
                    ) : (
                      <Tag color="default">Sem informação</Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Valor">
                    {selectedTicket.payment_value 
                      ? `R$ ${Number(selectedTicket.payment_value).toFixed(2)}` 
                      : 'N/A'}
                  </Descriptions.Item>
                  {selectedTicket.asaas_payment_id && (
                    <Descriptions.Item label="ID do Pagamento Asaas">
                      <Text code copyable>{selectedTicket.asaas_payment_id}</Text>
                    </Descriptions.Item>
                  )}
                </>
              )}
              
              <Descriptions.Item label="Notion">
                <Space>
                  <Input.Search
                    key={selectedTicket.id}
                    allowClear
                    enterButton="Salvar"
                    placeholder="https://..."
                    defaultValue={selectedTicket.notion_page_url ?? ''}
                    onSearch={handleSaveNotionLink}
                  />
                  {selectedTicket.notion_page_url && (
                    <Tooltip title="Abrir no Notion">
                      <Button
                        type="text"
                        icon={<LinkOutlined />}
                        href={selectedTicket.notion_page_url}
                        target="_blank"
                      />
                    </Tooltip>
                  )}
                </Space>
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">Histórico</Divider>
            {loadingMessages ? (
              <Paragraph>Carregando mensagens...</Paragraph>
            ) : messages.length === 0 ? (
              <Empty description="Sem mensagens até o momento" />
            ) : (
              <Timeline
                items={messages.map((item) => ({
                  color: item.is_internal ? 'purple' : 'blue',
                  children: (
                    <Space direction="vertical" style={{ width: '100%' }} size={4}>
                      <Space>
                        <FileTextOutlined />
                        <Text strong>
                          {item.is_internal ? 'Nota interna' : 'Mensagem'} • {dayjs(item.created_at).format('DD/MM/YYYY HH:mm')}
                        </Text>
                      </Space>
                      <Paragraph style={{ marginBottom: 0 }}>{item.message}</Paragraph>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {item.author?.full_name ?? 'Equipe FlowHub'} ({item.created_by_role})
                      </Text>
                    </Space>
                  ),
                }))}
              />
            )}

            <Form form={messageForm} layout="vertical" onFinish={handleSubmitInternalNote}>
              <Form.Item
                label="Registrar nota"
                name="message"
                rules={[{ required: true, message: 'Escreva uma mensagem' }]}
              >
                <Input.TextArea rows={3} placeholder="Atualização interna ou resposta para o cliente" />
              </Form.Item>
              <Form.Item name="is_internal" valuePropName="checked" initialValue={true}>
                <Checkbox>Nota interna</Checkbox>
              </Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={createMessage.isPending}>
                  Adicionar nota
                </Button>
                <Button onClick={() => messageForm.resetFields()}>Limpar</Button>
              </Space>
            </Form>
          </Space>
        )}
      </Drawer>
    </div>
  );
};
